import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import axios from "axios";
import {
    HeadingText,
    ParagraphText,
    UBInput,
    UBRadioBox,
} from "../../../../Libraries";
import TabAutoComplete from "../../../../Libraries/TabAutoComplete";
import {
    AppAmountChip,
    AppSelectOptionList,
} from "../../../common";
import AddCustomerModal from "../../../common/Modals/AddCustomerModal";
import {
    EasyloadAndPackages,
    fewPaymentMethods,
    sentenceCase,
} from "../../../../constants";
import { formatNum } from "../../../../oscar-pos-core/constants";
import { CloseIconTwo, CoinIcon, EditIcon, RupeeIcon, TickIcon, RechargePlaceholderIcon } from "../../../../assets/icons/icon";
import { useWalletContext } from "../../../../context/WalletContext";
import { useSnackbar } from "../../../../context/SnackbarContext";
import { analytics, triggerLogEvent } from "../../../../config/firebase";
import { phoneNumRegex } from "../../../../validations";
import formStyles from "../../SaleExpense/SaleExpenseForm/styles.module.css";
import rechargeFormStyles from "./RechargeFormSection.module.css";
import styles from "./Easyload.module.css";
import { useNavigate } from "react-router-dom";
import { handleApi } from "../../../../apis";
import { SEARCH_RECHARGE_CUSTOMER } from "../../../../apis/configuration";

const AmountSection = ({
    I18n,
    onChangeText,
    amountError,
    detail,
    ChipOptions,
    handleChip,
    chipActiveIndex,
    earnMoney,
    customers,
    selectedLoad,
    setState,
    setCustomer,
    onBack = () => { },
    isHiddenTelcoAndContact = false,
    contactError = "",
}) => {
    const [createCustomerModal, setCreateCustomerModal] = useState(false);
    const [initName, setInitname] = useState("");
    const [initPhone, setInitphone] = useState("");
    const [contactToUpdate, setContactToUpdate] = useState(null);
    const [isContactListOpen, setIsContactListOpen] = useState(false);
    const [rechargeCustomerSuggestions, setRechargeCustomerSuggestions] = useState([]);
    const [isSearchingRechargeCustomer, setIsSearchingRechargeCustomer] = useState(false);
    // Tracks logo URLs that failed to load, keyed per item, so those items
    // fall back to RechargePlaceholderIcon instead of a broken image.
    const [failedIcons, setFailedIcons] = useState({});
    const markIconFailed = (key) =>
        setFailedIcons((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
    const { walletData, handleGetWalletBalance, fetchAccountBalanceLoading } = useWalletContext();
    const { showSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const balance = walletData?.current_balance || 0;

    const rechargeSearchDebounceRef = useRef(null);
    const rechargeSearchCancelRef = useRef(null);

    useEffect(() => {
        handleGetWalletBalance(navigate);
    }, []);

    useEffect(() => {
        return () => {
            if (rechargeSearchDebounceRef.current) clearTimeout(rechargeSearchDebounceRef.current);
            if (rechargeSearchCancelRef.current) rechargeSearchCancelRef.current.cancel();
        };
    }, []);

    const searchRechargeCustomers = (typedValue) => {
        if (rechargeSearchDebounceRef.current) clearTimeout(rechargeSearchDebounceRef.current);
        if (rechargeSearchCancelRef.current) rechargeSearchCancelRef.current.cancel();

        const digits = (typedValue || "").replace(/[^0-9]/g, "");
        if (digits.length < 3) {
            setIsSearchingRechargeCustomer(false);
            setRechargeCustomerSuggestions([]);
            return;
        }

        rechargeSearchDebounceRef.current = setTimeout(async () => {
            setIsSearchingRechargeCustomer(true);
            rechargeSearchCancelRef.current = axios.CancelToken.source();
            try {
                const response = await handleApi(
                    SEARCH_RECHARGE_CUSTOMER,
                    { customer_phone: digits },
                    rechargeSearchCancelRef.current,
                    navigate,
                );
                setIsSearchingRechargeCustomer(false);
                setRechargeCustomerSuggestions(response?.customers?.length ? response.customers : []);
            } catch (err) {
                if (!axios.isCancel(err)) {
                    setIsSearchingRechargeCustomer(false);
                    setRechargeCustomerSuggestions([]);
                }
            }
        }, 300);
    };

    const availableTelcos = walletData?.available_telcos || [];
    const { billType, offer } = detail;

    const rechargeSuggestionList = useMemo(
        () =>
            rechargeCustomerSuggestions.map((item, index) => {
                const iconKey = `recharge_contact_${item.customer_phone || index}`;
                return {
                    label: item.customer_name || item.customer_phone || "",
                    value: item.customer_phone || "",
                    subLabel: item.customer_phone || "",
                    img:
                        item.logo_link && !failedIcons[iconKey] ? (
                            <img
                                src={item.logo_link}
                                alt={item.customer_name || ""}
                                width={28}
                                height={28}
                                style={{ objectFit: "contain" }}
                                onError={() => markIconFailed(iconKey)}
                            />
                        ) : (
                            <Box display="flex" alignItems="center" justifyContent="center" width="100%" height="100%">
                                <RechargePlaceholderIcon width={19} height={19} />
                            </Box>
                        ),
                    raw: item,
                };
            }),
        [rechargeCustomerSuggestions, failedIcons],
    );

    const contactList = useMemo(
        () =>
            (customers || []).map((item) => ({
                id: item.id,
                labelText: item.name,
                label: (
                    <ParagraphText className={formStyles.contactLabel}>
                        <span className="newFontColor">{item.name}</span>
                        <span className="newLightColor">
                            {item?.is_supplier ? I18n.supplier : I18n.customer}
                        </span>
                    </ParagraphText>
                ),
                value: item.name,
            })),
        [customers, I18n],
    );

    const logContactSelect = () => {
        triggerLogEvent(
            analytics,
            selectedLoad === EasyloadAndPackages.EASYLOAD
                ? "w_earn_ep_easyload_contact"
                : "w_earn_ep_packages_contact",
        );
    };

    const handleRechargeCustomerSelect = (item) => {
        logContactSelect();
        const localMatch = (customers || []).find((c) => c.phone === item.customer_phone);
        setCustomer(localMatch);
        setState((e) => ({
            ...e,
            customerName: item.customer_name || "",
            customerNumber: item.customer_phone || "",
        }));
        setRechargeCustomerSuggestions([]);
    };

    const handlePhoneInputChange = (typedValue) => {
        // While the contacts list is open, typing searches by name only —
        // don't overwrite the selected/typed number until an item is picked.
        if (isContactListOpen) return;
        const match = (customers || []).find((c) => c.phone === typedValue);
        if (match) {
            logContactSelect();
            setCustomer(match);
            setState((e) => ({
                ...e,
                customerName: match.name || "",
                customerNumber: match.phone || "",
            }));
        } else {
            setCustomer(undefined);
            setState((e) => ({
                ...e,
                customerName: typedValue,
                customerNumber: typedValue,
            }));
        }
        searchRechargeCustomers(typedValue);
    };


    const openCreateCustomerModal = (value) => {
        if (value?.match?.(phoneNumRegex) === null) {
            setInitname(value);
            setInitphone("");
        } else {
            setInitphone(value);
            setInitname("");
        }
        setCreateCustomerModal(true);
    };

    const handleCreateContact = (name) => {
        openCreateCustomerModal(name);
    };

    const handleAddCustomer = (payload) => {
        setCreateCustomerModal(false);
        setContactToUpdate(null);
        const c = (customers || []).find((x) => x.id === payload?.id);
        setCustomer(c ?? payload);
        setState((e) => ({
            ...e,
            customerName: payload?.name || "",
            customerNumber: payload?.phone || "",
        }));
    };


    let telco =
        availableTelcos.length > 0
            ? availableTelcos.find(
                (item) => item.name.toLowerCase() === detail?.platform.toLowerCase()
            )
            : null;
    let icon = (earnMoney?.easyloadPackages?.list || []).find(
        (item) =>
            item.name.toLowerCase() === detail?.platform?.toLowerCase(),
    );
    const { postpaid_commission, bundle_commission, commission } = telco;

    let comission =
        billType == "postpaid"
            ? postpaid_commission
            : offer
                ? bundle_commission
                : commission;
    const comissionAmount = (detail?.amount * comission) / 100
    const customerName = detail?.customerName || "";
    const customerNumber = detail?.customerNumber || "";

    const earningRule = telco?.earning_rule;


    const fromBundle = detail?.type?.toLowerCase() !== "easyload";
    const selectedEarningRule = Number(fromBundle ? earningRule?.bundle : earningRule?.recharge);
    const shouldShowPoints = earningRule != null && Number.isFinite(selectedEarningRule) && selectedEarningRule !== 0;
    const coins = shouldShowPoints ? Math.floor(selectedEarningRule * detail.amount * 10) : null;


    return (
        <Box className="accountModalBody">
            {detail?.type.toLowerCase() === "easyload" ?
                <Fragment>
                    <Box className={styles.easyLoadDetailRow}>
                        {icon?.logo && (
                            <Box className={styles.easyLoadDetailIconWrapper}>
                                {!failedIcons.detail_icon ? (
                                    <img
                                        src={icon?.logo}
                                        alt=""
                                        className={styles.easyLoadDetailIcon}
                                        onError={() => markIconFailed("detail_icon")}
                                    />
                                ) : (
                                    <RechargePlaceholderIcon width={19} height={19} />
                                )}
                            </Box>
                        )}
                        <Box className={styles.easyLoadDetailText}>
                            <ParagraphText className="fontSize14 newFontColor">
                                {customerName}
                            </ParagraphText>
                            <ParagraphText className="fontSize14 newLightColor">
                                {customerNumber}
                            </ParagraphText>
                        </Box>
                        <ParagraphText className={`fontSize12 newLightColor ${styles.editIcon}`}>
                            <EditIcon
                                id="easyload_edit_recharge_details_btn"
                                className="cursorPointer"
                                onClick={() => {
                                    triggerLogEvent(analytics, "w_earn_ep_easyload_edit");
                                    onBack();
                                }}
                            />
                        </ParagraphText>
                    </Box>
                    <Box className={`${rechargeFormStyles.amountInputWrapper} easyloadAmountInputWrapper`}>
                        <UBInput
                            inputId="easyload_amount_input"
                            placeholder={I18n.enter_amount || "Enter amount"}
                            onChange={(_, value) => onChangeText("amount", value)}
                            label={sentenceCase(I18n.enter_amount_trans)}
                            required
                            name="amount"
                            value={detail.amount}
                            onlyNumbers
                            disabled={fetchAccountBalanceLoading}
                            error={{ text: amountError, show: !!amountError }}
                            infoLabel={
                                <ParagraphText className={"fontSize12 newLightColor"}>
                                    {I18n.available_balance}:{" "}
                                    {fetchAccountBalanceLoading ? (
                                        <span className={styles.balanceSkeleton} />
                                    ) : (
                                        <span className="newLightColor">
                                            {`Rs. ${formatNum(Math.floor(balance || 0))}`}
                                        </span>
                                    )}
                                </ParagraphText>
                            }
                        />
                    </Box>
                    <Box className={styles.easyloadAmountChipWrapper}>
                        <AppAmountChip
                            data={ChipOptions}
                            handleChip={handleChip}
                            chipActiveIndex={chipActiveIndex}
                            disabled={fetchAccountBalanceLoading}
                        />
                    </Box>
                    <Box className={styles.earnPointsRow}>
                        <CoinIcon />
                        <ParagraphText className="fontSize12">
                            <span className="newFontColor">{`Rs. ${comissionAmount}`} {shouldShowPoints ? "+" : ""} </span>
                            {shouldShowPoints && <span className="mustardYellowShadeColor">({coins} {I18n.points})</span>}
                        </ParagraphText>
                    </Box>
                </Fragment>
                :
                <Box className={`${rechargeFormStyles.packageInputWrapper}`}>
                    <UBInput
                        inputId="easyload_package_input"
                        placeholder={I18n.select_package}
                        label={I18n.package_name}
                        name="name"
                        value={
                            detail.offer?.offer_name
                                ? `${detail.offer?.offer_name} ${detail?.amount ? `(Rs. ${formatNum(detail?.amount)})` : ""
                                }`
                                : ""
                        }
                        maxLength={72}
                        readOnly
                    />
                    {detail?.offer?.offer_key ? (
                        <Box className={`${rechargeFormStyles.packageNameIcon} easyloadPackageNameIcon`}>
                            <TickIcon />
                        </Box>
                    ) : null}
                    <Box
                        className={`${formStyles.tabAutoCompleteBox} tabAutoCompleteBox`}
                    >
                        <TabAutoComplete
                            id="easyload_package_contactlist"
                            list={isContactListOpen ? contactList : rechargeSuggestionList}
                            listHeader={isContactListOpen ? "" : I18n.results}
                            label={I18n.phone_number}
                            handleSelect={(item) => {
                                if (!item) {
                                    setCustomer(undefined);
                                    setState((e) => ({
                                        ...e,
                                        customerName: "",
                                        customerNumber: "",
                                    }));
                                    return;
                                }
                                if (item.raw) {
                                    handleRechargeCustomerSelect(item.raw);
                                    return;
                                }
                                const c = (customers || []).find((x) => x.id === item.id);
                                if (c?.phone) {
                                    logContactSelect();
                                    setCustomer(c);
                                    setState((e) => ({
                                        ...e,
                                        customerName: c?.name || item.labelText || "",
                                        customerNumber: c?.phone || "",
                                    }));
                                } else {
                                    setContactToUpdate(c);
                                    showSnackbar({ message: I18n.please_add_phone_number_to_continue, severity: "info" });
                                    setCreateCustomerModal(true);
                                }
                            }}
                            value={detail?.customerNumber || ""}
                            handleCreate={handleCreateContact}
                            onInputChange={handlePhoneInputChange}
                            onOpenChange={(_isOpen, isContactsMode) => setIsContactListOpen(isContactsMode)}
                            manualTrigger
                            openOnType
                            loading={!isContactListOpen && isSearchingRechargeCustomer}
                            numericWhenClosed
                            suffixLabel={I18n.contacts}
                            placeholder={I18n.phone_number}
                            asCreate={I18n.customer}
                            required
                            error={contactError}
                            handleStartLogEvent={() =>
                                triggerLogEvent(analytics, "w_req_contact_start")
                            }
                            manuallyFocusInput={false}
                            handleManuallyInput={() => { }}
                        />
                    </Box>
                </Box>
            }

            <AddCustomerModal
                isOpen={createCustomerModal}
                handleClose={() => {
                    setCreateCustomerModal(false);
                    setContactToUpdate(null);
                }}
                navigate={navigate}
                initialName={initName}
                initialPhone={initPhone}
                onSaveCallback={handleAddCustomer}
                type="in"
                title={
                    <ParagraphText className={"ms-1 textLowercase"}>
                        {I18n.customer}
                    </ParagraphText>
                }
                isTypeField={true}
                isOnlyPhoneList={true}
                customerToUpdate={contactToUpdate}
                handleLogEventCustomerCreated={() =>
                    triggerLogEvent(analytics, "w_req_customer_created")
                }
                handleLogEventCustomerUpdate={() =>
                    triggerLogEvent(analytics, "customer_data_updated")
                }
            />
        </Box>
    );
};

export default AmountSection;
